import {
	isValidElement,
	type ComponentProps,
	type ReactNode,
	useState
} from "react"
import { buttonVariants } from "./button"
import {
	Combobox,
	ComboboxCollection,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
	ComboboxTrigger
} from "./combobox"
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip"

export function SearchableSelect({
	options,
	children,
	searchable = true,
	disabled,
	empty = "No options found",
	placeholder = "Select...",
	onSearchChange,
	closeOnSelect,
	...props
}: {
	options: {
		value: string
		label: string
		display?: ReactNode
		tooltip?: ReactNode
		disabled?: boolean
	}[]
	children?: ReactNode
	searchable?: boolean
	disabled?: boolean
	empty?: ReactNode
	placeholder?: ReactNode
	onSearchChange?: (query: string) => void
	closeOnSelect?: boolean
} & (
	| { multiple: true; value: string[]; onChange: (value: string[]) => void }
	| { multiple?: false; value: string; onChange: (value: string) => void }
)) {
	const [open, setOpen] = useState(false)
	const [triggerElement, setTriggerElement] =
		useState<HTMLButtonElement | null>(null)
	const trigger = isValidElement<ComponentProps<"button">>(children)
		? children
		: undefined
	const selected = props.multiple ? props.value : [props.value]
	const selectedTooltip = props.multiple
		? undefined
		: options.find((option) => option.value === props.value)?.tooltip
	const triggerButton = (
		<ComboboxTrigger
			ref={setTriggerElement}
			render={trigger}
			showChevron={!trigger}
			className={
				trigger
					? undefined
					: buttonVariants({
							variant: "outline",
							className:
								"h-auto min-h-9 w-full justify-between whitespace-normal text-left"
						})
			}
		>
			{trigger
				? trigger.props.children
				: selected.length && selected.some(Boolean)
					? options
							.filter((option) => selected.includes(option.value))
							.map((option) => option.label)
							.join(", ")
					: placeholder}
		</ComboboxTrigger>
	)
	return (
		<Combobox<string, boolean>
			multiple={props.multiple ?? false}
			items={options.map((option) => option.value)}
			value={props.value || null}
			disabled={disabled}
			open={open}
			onOpenChange={setOpen}
			onInputValueChange={onSearchChange}
			itemToStringLabel={(value) =>
				options.find((option) => option.value === value)?.label ?? value
			}
			onValueChange={(next) => {
				if (props.multiple) {
					if (Array.isArray(next)) props.onChange(next)
				} else if (typeof next === "string") props.onChange(next)
				if (closeOnSelect || !props.multiple) setOpen(false)
			}}
		>
			{selectedTooltip ? (
				<Tooltip>
					<TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
					{!open && <TooltipContent>{selectedTooltip}</TooltipContent>}
				</Tooltip>
			) : (
				triggerButton
			)}
			<ComboboxContent
				// Keep modal pickers inside their dialog's pointer/focus boundary.
				container={
					triggerElement?.closest<HTMLElement>(
						'[role="dialog"], [role="alertdialog"]'
					) ?? undefined
				}
				align="start"
				className="w-75 min-w-0 max-w-[calc(100vw-2rem)]"
				onWheel={(event) => event.stopPropagation()}
			>
				{searchable && (
					<ComboboxInput placeholder="Search..." showTrigger={false} popup />
				)}
				<ComboboxList>
					<ComboboxCollection>
						{(value) => {
							const option = options.find((option) => option.value === value)
							if (!option) return null
							const item = (
								<ComboboxItem
									key={option.value}
									value={option.value}
									disabled={option.disabled}
								>
									{option.display ?? option.label}
								</ComboboxItem>
							)
							return option.tooltip ? (
								<Tooltip key={option.value}>
									<TooltipTrigger asChild>{item}</TooltipTrigger>
									<TooltipContent side="right">{option.tooltip}</TooltipContent>
								</Tooltip>
							) : (
								item
							)
						}}
					</ComboboxCollection>
					<ComboboxEmpty>{empty}</ComboboxEmpty>
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	)
}
